/* eslint-disable */
import React, { useState } from 'react';
import GenericCard from '@ui-components/Card/GenericCard';
import { GenericTable, GenericTableColumn } from '@ui-components/Table/GenericTable';
import { CloudPropagationNeighbor, CloudPropagationApiResponse } from './CloudPropagationData';
import { transformCloudPropagationData, transformClusterCloudPropagationData } from '../../../helpers/formattersShapedData';
import { ReactComponent as IconSettings } from '../../../../shared/assets/Icons/IconSettings.svg';
import GenericButton from '@ui-components/Button/GenericButton';
import CPModal from './CPModal/CPModal';
import EmptyItems from '@shared/components/EmptyItems';
import GenericAlert from '@ui-components/Alert/GenericAlert';
import { buildInboundPropagationRulePayload, createInboundPropagationRule, buildOutboundPropagationRulePayload, createOutboundPropagationRule, getCloudPropagationRuleDetails, updateInboundPropagationRule, updateOutboundPropagationRule } from '../request/request';
import { updateInboundPropagationRuleCluster, updateOutboundPropagationRuleCluster, createInboundPropagationRuleCluster, createOutboundPropagationRuleCluster, getCloudPropagationRulesByCluster } from '../Cluster/request/request';

interface CloudPropagationPageProps {
  data?: CloudPropagationNeighbor[] | CloudPropagationApiResponse;
  columns: GenericTableColumn<CloudPropagationNeighbor>[];
  error: string | null;
  onConfigure?: (row: CloudPropagationNeighbor) => void;
  getAccessTokenSilently?: () => Promise<string>;
  clusterId?: number; // Add clusterId for cluster mode
}

const CloudPropagationPage: React.FC<CloudPropagationPageProps> = ({
  data,
  columns,
  error,
  onConfigure,
  getAccessTokenSilently,
  clusterId,
}) => {
  // State for modal open/close and selected row
  const [isCPModalOpen, setIsCPModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<CloudPropagationNeighbor | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ruleDetails, setRuleDetails] = useState<any>(null);
  const [isLoadingRuleDetails, setIsLoadingRuleDetails] = useState(false);
  const [loadingRowId, setLoadingRowId] = useState<number | null>(null);
  const [ruleIds, setRuleIds] = useState<{ inboundId?: number; outboundId?: number }>({});

  // Alert state variables for success notifications
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState("");
  const [alertDescription, setAlertDescription] = useState("");
  const [alertVariant, setAlertVariant] = useState<"success" | "error" | "confirm" | undefined>(undefined);

  // Helper function to show success alerts
  const showSuccessAlert = (operation: 'create' | 'update', ruleType: 'inbound' | 'outbound', mode: 'standalone' | 'cluster') => {
    const operationText = operation === 'create' ? 'created' : 'updated';
    const modeText = mode === 'cluster' ? 'cluster' : '';
    
    setAlertTitle(`${ruleType.charAt(0).toUpperCase() + ruleType.slice(1)} propagation rule ${operationText} successfully`);
    setAlertDescription(`The ${ruleType} propagation rule has been ${operationText} successfully${modeText ? ` for the ${modeText}` : ''}.`);
    setAlertVariant("success");
    setIsAlertOpen(true);
    
    // Auto-close alert after 3 seconds
    setTimeout(() => {
      setIsAlertOpen(false);
    }, 3000);
  };

  // Transform data if it's an API response, otherwise use as-is
  const transformedData = React.useMemo(() => {
    if (!data) return [];
    
    // Check if data is already in the correct format
    if (Array.isArray(data) && data.length > 0 && 'bgpNeighborIp' in data[0]) {
      return data as CloudPropagationNeighbor[];
    }
    
    // Handle new API structure - wrapped in data property (current structure)
    if (typeof data === 'object' && 'data' in data) {
      if (clusterId) {
        // Use cluster-specific transformation
        return transformClusterCloudPropagationData(data as CloudPropagationApiResponse);
      } else {
        // Use regular transformation
        return transformCloudPropagationData(data as CloudPropagationApiResponse);
      }
    }
    
    // Handle direct array response (fallback)
    if (Array.isArray(data)) {
      if (clusterId) {
        // Use cluster-specific transformation
        return transformClusterCloudPropagationData(data);
      } else {
        // Use regular transformation
        return transformCloudPropagationData(data);
      }
    }
    
    return [];
  }, [data, clusterId]);

  // Ensure data is always an array to prevent "data is not iterable" error
  const safeData = Array.isArray(transformedData) ? transformedData : [];

  // Function to fetch rule details for a specific BGP neighbor (standalone mode)
  const fetchRuleDetails = async (bgpNeighborId: number, currentRow?: CloudPropagationNeighbor) => {
    if (!getAccessTokenSilently) return;
    
    setIsLoadingRuleDetails(true);
    setLoadingRowId(bgpNeighborId);
    try {
      const token = await getAccessTokenSilently();
      const response = await getCloudPropagationRuleDetails(token, bgpNeighborId);
      
      // Transform the API response to match our form structure
      const transformedData = transformRuleDetailsResponse(response.data.data, currentRow || selectedRow || undefined);
      
      setRuleDetails(transformedData);
    } catch (error: any) {
      console.error('Error fetching rule details:', error);
      setRuleDetails(null);
    } finally {
      setIsLoadingRuleDetails(false);
      setLoadingRowId(null);
    }
  };

  // Function to fetch cluster propagation rules (cluster mode)
  const fetchClusterRuleDetails = async (currentRow?: CloudPropagationNeighbor) => {
    if (!getAccessTokenSilently || !clusterId) return;
    
    // Check if we have a BGP neighbor ID in the current row
    if (!currentRow?.bgpNeighborId) {
      console.error('No BGP neighbor ID available for cluster mode');
      setRuleDetails(null);
      return;
    }
    
    setIsLoadingRuleDetails(true);
    setLoadingRowId(currentRow.bgpNeighborId);
    try {
      const token = await getAccessTokenSilently();
      // Use getCloudPropagationRuleDetails instead of getCloudPropagationRulesByCluster
      const response = await getCloudPropagationRuleDetails(token, currentRow.bgpNeighborId);
      
      // Transform the API response to match our form structure
      const transformedData = transformRuleDetailsResponse(response.data.data, currentRow || selectedRow || undefined);
      
      setRuleDetails(transformedData);
    } catch (error: any) {
      console.error('Error fetching cluster rule details:', error);
      setRuleDetails(null);
    } finally {
      setIsLoadingRuleDetails(false);
      setLoadingRowId(null);
    }
  };

  // Function to transform the API response to our form structure
  const transformRuleDetailsResponse = (apiData: any, currentSelectedRow?: CloudPropagationNeighbor) => {
    const result: any = {};
    const ids: { inboundId?: number; outboundId?: number } = {};
    
    // Since we're now using getCloudPropagationRuleDetails for both cluster and standalone modes,
    // the data structure is the same for both modes
    // Ensure apiData is an array
    const dataArray = Array.isArray(apiData) ? apiData : [apiData];
    
    // Check if any configuration exists
    const hasAnyConfig = dataArray.some((rule) => 
      (rule.propagationType === 'INBOUND' && rule.inbounds && rule.inbounds.length > 0) ||
      (rule.propagationType === 'OUTBOUND' && rule.outbounds && rule.outbounds.length > 0)
    );
    
    
    dataArray.forEach((rule) => {
      if (rule.propagationType === 'INBOUND' && rule.inbounds && rule.inbounds.length > 0) {
        // Store the first inbound rule ID for update operations (using the first one as reference)
        ids.inboundId = rule.inbounds[0].id;
        
        // Check if this is Telco mode by looking at the currentSelectedRow mode
        // The backend might return mode: null, so we rely on currentSelectedRow.mode for Telco detection
        // If currentSelectedRow mode is not available, we can also check if the data has Telco-specific fields
        let isTelco = currentSelectedRow?.mode === 'Telco';
        
        // Fallback: if currentSelectedRow mode is not available, check if the data has Telco-specific structure
        if (isTelco === undefined || isTelco === null) {
          // Check if the inbound data has Telco-specific fields like vrfName or routeTargets
          isTelco = !!(rule.inbounds[0].vrfName || rule.inbounds[0].routeTargets);
        }
        
        if (isTelco) {
          // Telco mode: Create VRF structure for all inbounds
          const vrfs = rule.inbounds.map((inbound: any) => {
            const policySelection = inbound.inboundType === 'specific' ? 'specific' : 'dynamic';
            const nextHopSelection = inbound.nextHopSettings?.[0]?.hopType === 'ENI' ? 'specific' : 'dynamic';
            
            return {
              name: inbound.vrfName || 'Default VRF',
              routeTargets: inbound.routeTargets ? inbound.routeTargets.split(',').map((rt: string) => rt.trim()).filter((rt: string) => rt) : [],
              newRouteTarget: '',
              routingTable: '',
              nextHopEni: '',
              policySelection: policySelection,
              nextHopSelection: nextHopSelection,
              routingTables: inbound.specificRoutes?.map((route: any) => route.routeTable) || [],
              enis: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.map((eni: any) => eni.elementValue) || [],
              routingTableTagName: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagName : '',
              routingTableTagValue: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagValue : '',
              nextHopTagName: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagName : '',
              nextHopTagValue: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagValue : '',
              expanded: true,
              // Map VPC ID from API response for Telco mode
              selectedVpc: inbound.vpcId || '',
              // Map inboundId from API response (id field) for updates
              inboundId: inbound.id || null,
            };
          });
          
          result.inbound = {
            vrfs: vrfs,
            // Add vpcId at top level for Telco mode (extract from first VRF's selectedVpc)
            selectedVpc: vrfs.length > 0 ? vrfs[0].selectedVpc : '',
          };
        } else {
          // Enterprise mode: Use direct field structure (take the first inbound for enterprise mode)
          const inbound = rule.inbounds[0];
          const policySelection = inbound.inboundType === 'specific' ? 'specific' : 'dynamic';
          const nextHopSelection = inbound.nextHopSettings?.[0]?.hopType === 'ENI' ? 'specific' : 'dynamic';
          
          result.inbound = {
            // Map inboundType to policySelection (form expects "specific" or "dynamic")
            policySelection: policySelection,
            
            // Map specific routes to routingTables array (form expects this exact field name)
            routingTables: inbound.specificRoutes?.map((route: any) => route.routeTable) || [],
            
            // Map dynamic routes (if any) - form expects these exact field names
            routingTableTagName: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagName : '',
            routingTableTagValue: inbound.dynamicRoutes?.length > 0 ? inbound.dynamicRoutes[0]?.tagValue : '',
            
            // Map next hop settings - form expects "specific" or "dynamic"
            nextHopSelection: nextHopSelection,
            
            // Map specific ENIs - form expects "enis" array
            enis: inbound.nextHopSettings?.[0]?.cloudElementInterfaces?.map((eni: any) => eni.elementValue) || [],
            
            // Map dynamic next hop tags - form expects these exact field names
            nextHopTagName: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagName : '',
            nextHopTagValue: inbound.nextHopSettings?.[0]?.dynamicRouteTags?.length > 0 ? inbound.nextHopSettings[0].dynamicRouteTags[0]?.hopTagValue : '',
            
            // Map VPC ID from API response
            selectedVpc: inbound.vpcId || '',
          };
        }
        
      }
      
      if (rule.propagationType === 'OUTBOUND' && rule.outbounds && rule.outbounds.length > 0) {
        const outbound = rule.outbounds[0]; // Take the first outbound rule
        
        // Store the outbound rule ID for update operations
        ids.outboundId = outbound.id;
        
        // Map outbound data to match form field names exactly
        result.outbound = {
          // Form expects these exact field names
          subnets: outbound.outboundSubnets?.map((subnet: any) => subnet.subnets) || [],
          bgpCommunity: outbound.bgpCommunity || '',
          nexthopIPAddress: outbound.nexthopIPAddress || '',
        };
      }
    });
    
    // Store the rule IDs for update operations
    setRuleIds(ids);
    
    // Store the hasAnyConfig flag in the result
    result.hasExistingConfig = hasAnyConfig;
    
    return result;
  };

  // Handle opening the modal and fetching rule details
  const handleOpenModal = async (row: CloudPropagationNeighbor) => {
    setSelectedRow(row);
    
    // Fetch rule details based on mode
    if (clusterId) {
      // Cluster mode: fetch cluster propagation rules
      await fetchClusterRuleDetails(row);
    } else if (row.bgpNeighborId) {
      // Standalone mode: fetch individual BGP neighbor rule details
      await fetchRuleDetails(row.bgpNeighborId, row);
    }
    
    // Only open modal after fetching is complete
    setIsCPModalOpen(true);
  };

  // Handle form submission
  const handleSubmit = async (formData: any) => {
    // Check if we have the required props for either cluster or standalone mode
    const isClusterMode = clusterId && getAccessTokenSilently;
    const isStandaloneMode = selectedRow?.bgpNeighborId && getAccessTokenSilently;

    if (!isClusterMode && !isStandaloneMode) {
      console.error('Missing required props for submission. Cluster mode:', isClusterMode, 'Standalone mode:', isStandaloneMode);
      return;
    }

    setIsSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const { inbound, outbound, activeTab, isUpdate, isClusterMode: isClusterUpdate, ruleType, payload } = formData;
      
      // Handle update operations
      if (isUpdate && ruleType && payload) {
        if (isClusterUpdate && clusterId) {
          // Cluster mode updates
          if (ruleType === 'inbound' && ruleIds.inboundId) {
            // For Telco mode, use create endpoint instead of update
            if (selectedRow?.mode === 'Telco') {
              await createInboundPropagationRuleCluster(token, clusterId, payload);
              showSuccessAlert('update', 'inbound', 'cluster');
            } else {
              // For Enterprise mode, use update endpoint
              await createInboundPropagationRuleCluster(token, clusterId, payload);
              showSuccessAlert('update', 'inbound', 'cluster');
            }
            // Add delay before closing modal to ensure alert is visible
            setTimeout(() => {
              setIsCPModalOpen(false);
              setSelectedRow(null);
            }, 500);
            return;
          } else if (ruleType === 'outbound' && ruleIds.outboundId) {
            await updateOutboundPropagationRuleCluster(token, clusterId, ruleIds.outboundId, payload);
            showSuccessAlert('update', 'outbound', 'cluster');
            // Add delay before closing modal to ensure alert is visible
            setTimeout(() => {
              setIsCPModalOpen(false);
              setSelectedRow(null);
            }, 500);
            return;
          }
        } else {
          // Standalone mode updates
          if (ruleType === 'inbound' && ruleIds.inboundId) {
            // For Telco mode, use create endpoint instead of update
            if (selectedRow?.mode === 'Telco') {
              await createInboundPropagationRule(token, Number(selectedRow?.bgpNeighborId), payload);
              showSuccessAlert('update', 'inbound', 'standalone');
            } else {
              // For Enterprise mode, use update endpoint
              await createInboundPropagationRule(token, Number(selectedRow?.bgpNeighborId), payload);
              showSuccessAlert('update', 'inbound', 'standalone');
            }
            // Add delay before closing modal to ensure alert is visible
            setTimeout(() => {
              setIsCPModalOpen(false);
              setSelectedRow(null);
            }, 500);
            return;
          } else if (ruleType === 'outbound' && ruleIds.outboundId) {
            await updateOutboundPropagationRule(token, Number(selectedRow?.bgpNeighborId), ruleIds.outboundId, payload);
            showSuccessAlert('update', 'outbound', 'standalone');
            // Add delay before closing modal to ensure alert is visible
            setTimeout(() => {
              setIsCPModalOpen(false);
              setSelectedRow(null);
            }, 500);
            return;
          }
        }
        setIsCPModalOpen(false);
        setSelectedRow(null);
        return;
      }
      
      // Handle cluster mode create operations
      if (isClusterMode && clusterId && activeTab && !isUpdate) {
        
        if (activeTab === 'inbound' && payload) {
          // For inbound, the payload should always have meaningful data if it was built
          await createInboundPropagationRuleCluster(token, clusterId, payload);
          showSuccessAlert('create', 'inbound', 'cluster');
          // Add delay before closing modal to ensure alert is visible
          setTimeout(() => {
            setIsCPModalOpen(false);
            setSelectedRow(null);
          }, 500);
        } else if (activeTab === 'outbound' && payload) {
          // Validate that payload has meaningful data
          const hasSubnets = payload.subnets && payload.subnets.length > 0;
          const hasBgpCommunity = payload.bgpCommunity && payload.bgpCommunity.trim() !== '';
          const hasNexthopIP = payload.nexthopIPAddress && payload.nexthopIPAddress.trim() !== '';
          
          
          if (hasSubnets || hasBgpCommunity || hasNexthopIP) {
            await createOutboundPropagationRuleCluster(token, clusterId, payload);
            showSuccessAlert('create', 'outbound', 'cluster');
            // Add delay before closing modal to ensure alert is visible
            setTimeout(() => {
              setIsCPModalOpen(false);
              setSelectedRow(null);
            }, 500);
          } else {
            console.error('Outbound payload is empty - no subnets, BGP community, or next-hop IP provided');
          }
        }
        return;
      }
      
      // Only execute standalone create logic if we're in standalone mode and not updating
      if (isStandaloneMode && selectedRow?.bgpNeighborId) {
        // Make API call based on the active tab
        if (activeTab === 'inbound') {
          // Build payload for inbound propagation rule
          const inboundPayload = buildInboundPropagationRulePayload(
            selectedRow.bgpNeighborId, 
            inbound, 
            selectedRow?.mode === 'Telco'
          );

          if (inboundPayload) {
            await createInboundPropagationRule(token, selectedRow.bgpNeighborId, inboundPayload);
            showSuccessAlert('create', 'inbound', 'standalone');
            // Add delay before closing modal to ensure alert is visible
            setTimeout(() => {
              setIsCPModalOpen(false);
              setSelectedRow(null);
            }, 500);
          }
        } else if (activeTab === 'outbound') {
          // Build payload for outbound propagation rule
          const outboundPayload = buildOutboundPropagationRulePayload(
            selectedRow.bgpNeighborId,
            outbound,
            selectedRow?.mode === 'Telco'
          );

          if (outboundPayload) {
            await createOutboundPropagationRule(token, selectedRow.bgpNeighborId, outboundPayload);
            showSuccessAlert('create', 'outbound', 'standalone');
            // Add delay before closing modal to ensure alert is visible
            setTimeout(() => {
              setIsCPModalOpen(false);
              setSelectedRow(null);
            }, 500);
          }
        }
      }
      
      // Close modal and refresh data if needed
      setIsCPModalOpen(false);
      setSelectedRow(null);
      
      // You might want to trigger a refresh of the data here
      // onRefresh?.();
    } catch (error) {
      console.error('Error creating propagation rule:', error);
      // You might want to show an error message to the user here
    } finally {
      setIsSubmitting(false);
    }
  };

  const tableColumns =
    columns.map((col) =>
      col.header === 'ACTIONS'
        ? {
            ...col,
            renderCell: (row: CloudPropagationNeighbor) => {
              const isThisRowLoading = loadingRowId === row.bgpNeighborId;
              return (
                <GenericButton
                  icon={isThisRowLoading ? undefined : <IconSettings className="w-4 h-4 text-semantic-text-secondary" />}
                  iconPosition="right"
                  variant="secondary"
                  label="Configure"
                  shape="filled"
                  size="sm"
                  aria-label="Configure"
                  loading={isThisRowLoading}
                  disabled={isThisRowLoading}
                  onClick={() => {
                    handleOpenModal(row);
                    onConfigure?.(row);
                  }}
                />
              );
            },
          }
        : col
    );

  return (
    <div>
      <GenericCard isCollapsible={false}>
        <div className="flex flex-col gap-6 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Cloud Propagation</h2>
            <span className="text-semantic-text-secondary text-sm">
              Rules are automatically created when BGP neighbors are added
            </span>
          </div>
          
          {/* Show table for standalone mode */}
          {error ? (
            <EmptyItems />
          ) : (
            <GenericTable
              columns={tableColumns}
              data={safeData}
              initialRowsPerPage={5}
              rowsPerPageOptions={[5, 10]}
              width="100%"
              tableClassName="bg-white"
            />
          )}
        </div>
      </GenericCard>
      {/* Cloud Propagation Modal */}
      <CPModal
        isOpen={isCPModalOpen}
        onClose={() => {
          setIsCPModalOpen(false);
          setSelectedRow(null);
          setRuleDetails(null); // Clear rule details when closing
          setRuleIds({}); // Clear rule IDs when closing
        }}
        onSubmit={handleSubmit}
        initialValues={(() => {
          const values = ruleDetails || selectedRow || {};
          return values;
        })()}
        tag={selectedRow?.mode}
        bgpNeighborId={selectedRow?.bgpNeighborId}
        reflectorId={selectedRow?.reflectorInstanceId}
        isSubmitting={isSubmitting || isLoadingRuleDetails}
        hasExistingConfig={(() => {
          const hasConfig = ruleDetails?.hasExistingConfig || false;
          return hasConfig;
        })()}
        {...(clusterId && { clusterId })}
        {...(getAccessTokenSilently && { getAccessTokenSilently })}
      />
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => setIsAlertOpen(false)}
        title={alertTitle}
        description={alertDescription}
        variant={alertVariant}
        confirmLabel="OK"
      />
    </div>
  );
};

export default CloudPropagationPage;
